import { ClickAwayListener, Popper } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon from '@/src/components/common/Elements/Icon/Icon';
import Label from '@/src/components/common/Elements/Label/Label';
import HighlightSearchTerm from '@/src/components/project/Common/Typography/HighlightSearchTerm';
import { getUserWithFullNameAndEmaiDisplayValue } from '@/src/components/project/Inbox2/models/EmailEntry';
import {
  UserWithFullNameAndEmail,
  UserWithFullNameAndEmailAndAvatar,
} from '@/src/components/project/Inbox2/types';
import { usePopper } from '@/src/hooks/usePopper';
import { getValueWithEllipsis } from '@/src/util/strings';

const RecipientListItem = ({ recipient }) => {
  return (
    <React.Fragment>
      <span>{getUserWithFullNameAndEmaiDisplayValue(recipient)}</span>
    </React.Fragment>
  );
};

const RecipientList = ({ recipientList = [] }) => {
  return (
    <p className={clsx('recipientList', 'tw-m-0 tw-text-cfo-black')}>
      {recipientList.map((recipient, index) => (
        <React.Fragment key={`rli_${index}`}>
          <RecipientListItem key={`${recipient.fullName}_${index}`} recipient={recipient} />
          {index < recipientList.length - 1 && (
            <React.Fragment>
              , <br />
            </React.Fragment>
          )}
        </React.Fragment>
      ))}
    </p>
  );
};

type RecipientListPopperProps = {
  emailEntry: {
    from: UserWithFullNameAndEmailAndAvatar[];
    to: UserWithFullNameAndEmail[];
    cc: UserWithFullNameAndEmail[];
  };
};

const RecipientListPopper = ({ emailEntry }: RecipientListPopperProps) => {
  const [isPopperOpen, popperAnchorEl, togglePopper] = usePopper({ anchorEl: null, open: false });
  // const emailTo = emailEntry.to ? getEmailObjects(emailEntry.to) : [];
  // const emailCc = emailEntry.cc ? getEmailObjects(emailEntry.cc) : [];
  // const emailFrom = getEmailObject(emailEntry.from);
  const emailToPreview = getValueWithEllipsis(
    emailEntry.to.map((ee) => getUserWithFullNameAndEmaiDisplayValue(ee)).join(','),
    60
  );

  return (
    <div className={clsx('tw-flex tw-items-center')}>
      {/* <p class="m0">To: { toRecipient} <strong class={`m0 p0`}>{`${toRecipientlength > 0 ? '+'+toRecipientlength+' more' : ''}`}</strong></p> */}

      <p className={clsx('tw-m-0', 'tw-text-cfo-black', 'tw-leading-[20px]')}>
        To: <HighlightSearchTerm>{emailToPreview}</HighlightSearchTerm>
      </p>
      <Button
        id={'ellipsis_action'}
        className={clsx(
          'tw-group',
          'tw-mr-0 tw-ml-[5px]',
          'tw-text-cfo-black',
          'hover:tw-text-cfo-blue-600',
          isPopperOpen && 'tw-text-cfo-blue-600'
        )}
        onClick={togglePopper}
      >
        <Icon
          className={clsx('tw-text-[24px]', 'group-hover:tw-text-cfo-blue-600')}
          icon={'arrow_drop_down'}
        />
      </Button>
      <Popper
        open={isPopperOpen}
        anchorEl={popperAnchorEl}
        placement="bottom-start"
        style={{ zIndex: 100 }}
      >
        <ClickAwayListener onClickAway={() => togglePopper(null)}>
          <div
            className={clsx(
              'recipient--modal',
              'tw-w-[517px]',
              'tw-p-[10px]',
              'tw-rounded-[5px] tw-border-[1px] tw-border-solid tw-border-cfo-grey-500',
              'tw-bg-white tw-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] tw-box-border'
            )}
          >
            <Label
              label={'From:'}
              mainContainerClasses={clsx('tw-mb-[10px]')}
              labelClasses={clsx(
                'tw-min-w-[40px]',
                'tw-text-right',
                'tw-mr-[10px]',
                'tw-font-brandonTextRegular'
              )}
            >
              <RecipientList recipientList={emailEntry.from} />
            </Label>

            <Label
              label={'To:'}
              mainContainerClasses={clsx('tw-mb-[10px]')}
              labelClasses={clsx(
                'tw-min-w-[40px]',
                'tw-text-right',
                'tw-mr-[10px]',
                'tw-font-brandonTextRegular'
              )}
            >
              <RecipientList recipientList={emailEntry.to} />
            </Label>

            {emailEntry.cc.length > 0 && (
              <React.Fragment>
                <Label
                  label={'Cc:'}
                  mainContainerClasses={clsx('tw-mb-[10px]')}
                  labelClasses={clsx(
                    'tw-min-w-[40px]',
                    'tw-text-right',
                    'tw-mr-[10px]',
                    'tw-font-brandonTextRegular'
                  )}
                >
                  <RecipientList recipientList={emailEntry.cc} />
                </Label>
              </React.Fragment>
            )}
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default RecipientListPopper;
